
import { supabase } from '@/integrations/supabase/client';
import { Comment } from '@/types/social';

// Cache for profiles to avoid redundant fetches
const profileCache = new Map<string, any>();

/**
 * Enriches a comment with profile data
 * @param comment The comment to enrich
 * @returns A promise that resolves to the enriched comment
 */
export async function enrichCommentWithProfile(comment: Comment): Promise<Comment> {
  try {
    // We should only have either user_id OR anonymous_id, not both
    // If user_id exists, prioritize that over anonymous_id
    if (comment.user_id) {
      // Clear any anonymous_id if user_id exists for consistency
      comment.anonymous_id = null;
      
      // Check if we already have this profile in cache
      if (profileCache.has(comment.user_id)) {
        const cachedProfile = profileCache.get(comment.user_id);
        return {
          ...comment,
          profile: cachedProfile
        };
      }
      
      // If not in cache, fetch from database
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('id, name')
        .eq('id', comment.user_id)
        .single();
        
      if (error) {
        console.error('Error fetching profile for comment:', error);
        const fallbackProfile = {
          id: comment.user_id,
          name: 'User',
          avatar_url: null
        };
        
        // Cache even fallback profiles to avoid repeated fetches
        profileCache.set(comment.user_id, fallbackProfile);
        
        return {
          ...comment,
          profile: fallbackProfile
        };
      }
      
      if (profileData) {
        const profile = {
          id: profileData.id,
          name: profileData.name || 'User',
          avatar_url: null // Avatars handled separately
        };
        
        // Cache the profile
        profileCache.set(comment.user_id, profile);
        
        return {
          ...comment,
          profile: profile
        };
      }
    } 
    
    // Handle anonymous comments
    if (!comment.user_id && comment.anonymous_id) {
      const anonProfile = {
        id: null,
        name: 'Anonymous User',
        avatar_url: null
      };
      
      return {
        ...comment,
        profile: anonProfile
      };
    }
    
    // Fallback for any other case
    return {
      ...comment,
      profile: {
        id: null,
        name: 'User',
        avatar_url: null
      }
    };
  } catch (error) {
    console.error('Error enriching comment with profile data:', error);
    return {
      ...comment,
      profile: {
        id: null,
        name: 'User',
        avatar_url: null
      }
    };
  }
}

/**
 * Clear the profile cache (useful when user data changes)
 */
export function clearProfileCache(): void {
  profileCache.clear();
}
